import { Storage } from "@/services/Storage";
import axios from "axios";
import { Device } from "@/services/Device";
import { hardwareBridge } from '@/services/hardwareBridge';
import { responses } from '@/utils/responses';

export default {
    name: 'backOffice',
    state: {},
    getters: {},
    mutations: {},
    actions: {
        /**
         * Open the Online BackOffice / Lytning Core for the current store
         * via a session-id deeplink.
         *
         * <p>Replaces the legacy `LytningCore://open?auth=<base64-payload>`
         * URL which carried the user's plaintext password across the OS
         * protocol boundary. The sessionId is opaque (256-bit SecureRandom)
         * and minted at login time by `POST /posAuth/{DB}/login` on
         * Franchise V2. The receiver hydrates the user via
         * `GET /posAuth/{DB}/session/{id}`.
         *
         * <p>On backend / network failure we deliberately open the bare
         * BackOffice URL (without `?auth=`) so the user can still sign in
         * manually rather than silently reintroducing the password-bearing
         * path.
         *
         * @param context
         * @param {{name: string}} [page] target BackOffice route; defaults to `stock`
         */
        openOnlineBackOffice: async (context, page = { name: `stock` }) => {
            const isLinux = navigator.userAgent.toLowerCase().includes('linux') ||
                (window?.process?.versions?.electron && process.platform === 'linux');
            const isElectron = !!(window?.hardwareIPC || window?.electronBridge || window?.process?.versions?.electron);
            const pageName = (page && page.name) || 'stock';
            console.log(`[deeplink][openOnlineBackOffice] invoked page=${pageName} isElectron=${isElectron}`);

            let store = null;
            let session = null;
            try {
                store = await Storage.getDefaultStore();
                console.log(`[deeplink][openOnlineBackOffice] store=${store && store.StoreDB ? store.StoreDB : 'undefined'} storeName=${store && store.StoreName ? store.StoreName : 'undefined'}`);
                if (!store || !store.StoreDB) {
                    throw new Error('No default store configured');
                }
                session = await Storage.getCurrentSession(store.StoreDB);
                console.log('[deeplink][openOnlineBackOffice] session=' + JSON.stringify(session) + ` expectedStoreDb=${store.StoreDB}`);
                if (!session || !session.sessionId) {
                    throw new Error('No deeplink session for this store');
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[deeplink][openOnlineBackOffice] session open failed - opening bare URL', e);
                console.warn(`[deeplink][openOnlineBackOffice] falling back to bare URL because: ${e && e.message ? e.message : e}`);
                try {
                    if (isElectron && window?.electronBridge?.openExternal) {
                        await window.electronBridge.openExternal('LytningCore://open');
                    } else {
                        window.open('LytningCore://open', '_blank');
                    }
                } catch (_) { /* best-effort */ }
                return { ok: false, reason: 'no-session' };
            }

            // sessionId is opaque; the receiver fetches user / store
            // details itself. The plaintext password NEVER leaves this
            // process.
            const protocolUrl = `LytningCore://open?session=${encodeURIComponent(session.sessionId)}`
                              + `&store=${encodeURIComponent(store.StoreDB)}`
                              + `&page=${encodeURIComponent(pageName)}`;

            console.log('[deeplink][openOnlineBackOffice] opening session URL', {
                store: store.StoreDB,
                page: pageName,
                sessionIdPrefix: session.sessionId.slice(0, 8) + '...'
            });
            console.log('[openOnlineBackOffice] Opening BackOffice with deep link...');
            console.log('[openOnlineBackOffice] Store:', store.StoreName);

            try {
                if (isElectron) {
                    // Method 1: Write to shared file for BackOffice's file watcher (most reliable)
                    // This is the PRIMARY method on Linux since openExternal silently fails.
                    // The shared file now carries the session URL (same shape as the protocol
                    // URL) instead of a base64-encoded user blob.
                    try {
                        if (window?.electronBridge?.hardwareInvoke) {
                            const writeResult = await window.electronBridge.hardwareInvoke('backoffice:writeAuthFile', { protocolUrl });
                            console.log('[deeplink][openOnlineBackOffice] auth file written ok=' + !!(writeResult && writeResult.ok !== false), writeResult);
                            console.log('[openOnlineBackOffice] Auth file written successfully');

                            // On Linux, auth file + launch the app so it can pick up the auth file
                            if (isLinux) {
                                console.log('[openOnlineBackOffice] Linux detected, launching Lytning Core...');

                                try {
                                    const launchResult = await window.electronBridge.hardwareInvoke('backoffice:launchApp', {});
                                    console.log('[deeplink][openOnlineBackOffice] linux launch result', launchResult);
                                    console.log('[openOnlineBackOffice] Launch result:', launchResult);
                                    // Surface install-missing / launch failure in plain language (Ticket #22077)
                                    if (launchResult && launchResult.ok === false) {
                                        const msg = launchResult.error
                                            || 'Lyt-ning Back Office could not be opened. Install Lyt-ning Core, then try again.';
                                        responses.showError(msg);
                                        return { ok: false, source: 'auth-file', launch: launchResult };
                                    }
                                    // Confirm launch so cashiers know Core is opening even if the window is slow
                                    responses.showInfo('Back Office opened in a new window');
                                } catch (launchErr) {
                                    console.warn('[openOnlineBackOffice] Failed to launch app:', launchErr);
                                    responses.showError('Lyt-ning Back Office could not be opened. Install Lyt-ning Core, then try again.');
                                    return { ok: false, source: 'auth-file', launchError: String(launchErr && launchErr.message ? launchErr.message : launchErr) };
                                }

                                return { ok: true, source: 'auth-file' };
                            }
                        }
                    } catch (fileErr) {
                        console.warn('[openOnlineBackOffice] Failed to write auth file:', fileErr);
                    }

                    // Method 2: Try to open via protocol (Windows only - silently fails on Linux)
                    if (!isLinux && window?.electronBridge?.openExternal) {
                        console.log('[openOnlineBackOffice] Opening protocol URL:', protocolUrl.substring(0, 80) + '...');
                        await window.electronBridge.openExternal(protocolUrl);
                        return { ok: true };
                    }
                }
            } catch (e) {
                console.warn('openOnlineBackOffice deep link failed, falling back to web URL:', e);
                const storeLink = 'https://apps.microsoft.com/detail/XP9MDB2QR2SPZM?hl=en-US&gl=ZM&ocid=pdpshare';
                if (window?.electronBridge?.openExternal) {
                    await window.electronBridge.openExternal(storeLink);
                } else {
                    window.open(storeLink, '_blank');
                }
            }
        },
        searchExpenseAccounts: async (context, accountName) => {
            let store = await Storage.getDefaultStore();
            return axios.get(backOfficeTomcatApi + `chartOfAccounts/${store.StoreDB}/searchExpenseAccounts?accountName=` + accountName)
                .then(({ data }) => data).catch(err => errorHandler.tomcatError(err));
        },
        capturePayout: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            let pcName = (window?.hardwareIPC || window?.electronBridge) ? await hardwareBridge.getPcName() : await Device.getPCName();

            payload.User = user.Username;
            payload.PC = pcName;
            return axios.post(backOfficeTomcatApi + `Payments/${store.StoreDB}/storePayout`, payload)
                .then(({ data }) => data).catch(err => errorHandler.tomcatError(err));
        },
        captureCashDrop: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            let settings = await Storage.getDeviceSettings();
            payload.user = user.Username;
            payload.pc = settings.PC;
            return axios.post(backOfficeTomcatApi + `safeFunctions/${store.StoreDB}/cashierDepositToSafe`, payload)
                .then(({ data }) => data).catch(err => errorHandler.tomcatError(err));
        },
        getCashSafes: async () => {
            let store = await Storage.getDefaultStore();
            return axios.get(backOfficeTomcatApi + `chartOfAccounts/${store.StoreDB}/getSafeByType?type=CASH`)
                .then(({ data }) => data).catch(err => errorHandler.tomcatError(err))
        }
    }
}
